import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    CheckCircle, 
    XCircle, 
    ChevronDown, 
    ChevronUp, 
    Award, 
    Home,
    RotateCcw,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { defaultQuestions, UserData, AdminQuestionItem, Question } from './quizData';

// --- INTERFACES ---
interface LocationState {
    score: number;
    answers: (string | number | null)[];
    userData: UserData;
}

const Result: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState;

    const [expandedReview, setExpandedReview] = useState<number | null>(null);
    const [finalQuestions, setFinalQuestions] = useState<Question[]>([]);
    
    // --- STATE UNTUK PAGINATION ---
    const [currentPage, setCurrentPage] = useState(1);
    const questionsPerPage = 5;

    useEffect(() => {
        if (!state) {
            navigate('/');
            return;
        }

        // --- LOGIKA SINKRONISASI SOAL ---
        const adminQuestions = localStorage.getItem('admin_questions');
        let questionsToReview: Question[] = [];

        if (adminQuestions) {
            const parsedAdminData: AdminQuestionItem[] = JSON.parse(adminQuestions);
            questionsToReview = parsedAdminData.map(q => {
                let correctIndex = 0;
                if (q.correctAnswer === 'B') correctIndex = 1;
                else if (q.correctAnswer === 'C') correctIndex = 2;
                else if (q.correctAnswer === 'D') correctIndex = 3;

                return {
                    id: q.id,
                    type: q.type as any,
                    question: q.questionText,
                    options: q.type !== 'essay' ? [q.optionA, q.optionB, q.optionC, q.optionD] : [],
                    correct: correctIndex,
                    keyAnswer: q.referenceAnswer,
                    explanation: "Pembahasan belum tersedia untuk soal ini."
                };
            });
        }

        if (questionsToReview.length === 0) {
            questionsToReview = defaultQuestions;
        }
        
        setFinalQuestions(questionsToReview);
    }, [state, navigate]);

    if (!state || finalQuestions.length === 0) return null;

    const { score, answers } = state;

    // Menentukan Level & Pesan Motivasi
    let resultLevel = 'BASIC';
    let message = "Keep practicing! You're just getting started.";
    let iconColor = "text-yellow-500";
    let borderColor = "border-yellow-200";

    if (score >= 81) {
        resultLevel = 'PROFICIENT';
        message = "Outstanding work! You're ready for the next challenge.";
        iconColor = "text-green-500";
        borderColor = "border-green-200";
    } else if (score >= 41) {
        resultLevel = 'INDEPENDENT';
        message = "Great job! You have a solid foundation.";
        iconColor = "text-blue-500";
        borderColor = "border-blue-200";
    }

    // --- LOGIKA PAGINATION ---
    const indexOfLastQuestion = currentPage * questionsPerPage;
    const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
    const currentQuestions = finalQuestions.slice(indexOfFirstQuestion, indexOfLastQuestion);
    const totalPages = Math.ceil(finalQuestions.length / questionsPerPage);

    const toggleAccordion = (index: number) => {
        setExpandedReview(expandedReview === index ? null : index);
    };

    // Fungsi ganti halaman dan scroll ke atas list
    const paginate = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        setExpandedReview(null); // Tutup accordion saat pindah halaman
        
        // Opsional: Scroll sedikit ke atas agar user sadar halaman berubah
        const reviewSection = document.getElementById('review-section');
        if (reviewSection) {
            reviewSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-poppins py-10 px-4 md:px-8 text-gray-800">
            <div className="max-w-6xl mx-auto">
                
                {/* HEADER */}
                <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-[#1E293B]">Assessment Result</h1>
                        <p className="text-gray-500 text-sm mt-1">Here is the detailed report of your recent quiz.</p>
                    </div>
                    <button 
                        onClick={() => navigate('/user/dashboard')}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-full text-gray-600 font-medium hover:bg-gray-50 transition shadow-sm text-sm"
                    >
                        <Home size={18} /> Back to Dashboard
                    </button>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    
                    {/* === LEFT COLUMN: SCORE CARD === */}
                    <div className="lg:col-span-1 space-y-6 sticky top-8">
                        
                        {/* SCORE CARD */}
                        <div className="bg-white rounded-[32px] p-8 shadow-sm border border-gray-100 flex flex-col items-center text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#FF0000] to-[#B91C1C]"></div>
                            
                            <div className={`w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center mb-4 border-2 ${borderColor}`}>
                                <Award size={40} className={iconColor} />
                            </div>

                            <h2 className="text-gray-400 font-medium text-sm uppercase tracking-widest mb-2">Total Score</h2>
                            <div className="flex items-baseline justify-center mb-2">
                                <span className="text-7xl font-extrabold text-[#1E293B] tracking-tight">{score}</span>
                                <span className="text-2xl text-gray-300 font-bold">/100</span>
                            </div>

                            <div className="mt-4 px-4 py-2 bg-gray-50 rounded-xl text-center">
                                <span className="block text-xs text-gray-400 font-bold uppercase mb-1">Your Level</span>
                                <span className="text-xl font-bold text-[#B91C1C]">{resultLevel}</span>
                            </div>

                            <p className="text-sm text-gray-500 mt-6 italic">"{message}"</p>
                        </div>

                        {/* [DIHAPUS] Bagian "Ready to improve?" / "Take a Class" sudah dihapus sesuai permintaan */}
                        
                    </div>

                    {/* === RIGHT COLUMN: REVIEW === */}
                    <div className="lg:col-span-2" id="review-section">
                        <div className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden flex flex-col min-h-[600px]">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                <h3 className="font-bold text-lg text-[#1E293B]">Review Answers</h3>
                                <div className="flex items-center gap-4 text-xs font-bold">
                                    <div className="flex items-center gap-1 text-green-600">
                                        <CheckCircle size={14} /> Correct
                                    </div>
                                    <div className="flex items-center gap-1 text-red-500">
                                        <XCircle size={14} /> Incorrect
                                    </div>
                                </div>
                            </div>

                            {/* LIST SOAL (Hanya menampilkan 5 soal berdasarkan halaman aktif) */}
                            <div className="p-6 space-y-4 flex-1">
                                {currentQuestions.map((q, idx) => {
                                    // Menghitung index asli agar nomor soal tetap urut (misal hal 2 mulai dari no 6)
                                    const actualIndex = indexOfFirstQuestion + idx;
                                    const ans = answers[actualIndex];
                                    let isCorrect = false;

                                    // Validasi Jawaban
                                    if (q.type === 'essay') {
                                        if (typeof ans === 'string' && q.keyAnswer) {
                                            if (ans.toLowerCase().includes(q.keyAnswer.toLowerCase())) isCorrect = true;
                                        }
                                    } else { 
                                        if (ans === q.correct) isCorrect = true; 
                                    }

                                    const isOpen = expandedReview === actualIndex;

                                    return (
                                        <div 
                                            key={q.id} 
                                            className={`
                                                border rounded-2xl transition-all duration-300 overflow-hidden
                                                ${isOpen ? 'border-gray-300 shadow-md' : 'border-gray-100 hover:border-gray-200'}
                                            `}
                                        >
                                            {/* QUESTION HEADER */}
                                            <div 
                                                onClick={() => toggleAccordion(actualIndex)}
                                                className="flex items-start gap-4 p-5 cursor-pointer bg-white"
                                            >
                                                {/* Icon Indicator */}
                                                <div className="shrink-0 mt-0.5">
                                                    {isCorrect ? (
                                                        <CheckCircle className="text-green-500" size={24} />
                                                    ) : (
                                                        <XCircle className="text-red-500" size={24} />
                                                    )}
                                                </div>

                                                {/* Question Text */}
                                                <div className="flex-1">
                                                    <p className="font-medium text-[#334155] text-[15px] leading-relaxed">
                                                        <span className="font-bold text-gray-900 mr-2">{actualIndex + 1}.</span>
                                                        {q.question}
                                                    </p>
                                                    <p className={`text-xs mt-1 font-semibold ${isCorrect ? 'text-green-600' : 'text-red-500'}`}>
                                                        {isCorrect ? 'Correct Answer' : 'Wrong Answer'}
                                                    </p>
                                                </div>

                                                {/* Arrow */}
                                                <div className="text-gray-400 shrink-0">
                                                    {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                </div>
                                            </div>

                                            {/* EXPANDED CONTENT (DETAILS) */}
                                            {isOpen && (
                                                <div className="bg-gray-50 border-t border-gray-100 p-5 text-sm">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                        {/* Jawaban User */}
                                                        <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Your Answer</p>
                                                            <p className={`font-semibold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                                                                {q.type === 'essay' 
                                                                    ? (ans || '-') 
                                                                    : (typeof ans === 'number' ? q.options?.[ans] : 'No Answer')
                                                                }
                                                            </p>
                                                        </div>

                                                        {/* Jawaban Benar (Jika salah) */}
                                                        {!isCorrect && (
                                                            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                                                <p className="text-xs text-green-600 uppercase font-bold mb-1">Correct Answer</p>
                                                                <p className="font-semibold text-green-800">
                                                                    {q.type === 'essay' 
                                                                        ? q.keyAnswer 
                                                                        : (q.correct !== undefined ? q.options?.[q.correct] : '-')
                                                                    }
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Penjelasan */}
                                                    <div className="bg-white p-4 rounded-xl border border-gray-200">
                                                        <p className="text-xs text-blue-600 uppercase font-bold mb-2 flex items-center gap-1">
                                                            <span className="bg-blue-100 px-1.5 py-0.5 rounded">Explanation</span>
                                                        </p>
                                                        <p className="text-gray-600 leading-relaxed">
                                                            {q.explanation || "No explanation provided for this question."}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* --- PAGINATION BUTTONS --- */}
                            {totalPages > 1 && (
                                <div className="p-6 border-t border-gray-100 flex items-center justify-center gap-2 bg-gray-50/30">
                                    {/* Tombol Previous */}
                                    <button 
                                        onClick={() => paginate(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>

                                    {/* Generate Page Numbers (1, 2, 3...) */}
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                                        <button
                                            key={number}
                                            onClick={() => paginate(number)}
                                            className={`
                                                w-10 h-10 flex items-center justify-center rounded-lg font-bold text-sm transition shadow-sm
                                                ${currentPage === number 
                                                    ? 'bg-[#B91C1C] text-white border border-[#B91C1C] ring-2 ring-red-100' 
                                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-[#B91C1C]'}
                                            `}
                                        >
                                            {number}
                                        </button>
                                    ))}

                                    {/* Tombol Next */}
                                    <button 
                                        onClick={() => paginate(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            )}

                        </div>

                        {/* MOBILE ONLY ACTION (Bottom) */}
                        <div className="mt-8 lg:hidden text-center">
                            <button 
                                onClick={() => window.location.reload()} 
                                className="text-gray-500 text-sm font-medium hover:text-gray-800 flex items-center justify-center gap-2 mx-auto"
                            >
                                <RotateCcw size={16} /> Retake Assessment
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Result;